import { useEffect, useState } from "react";
import useRanking from "../../hooks/useRanking";
import useUser from "../../hooks/useUser";
import toast, { Toaster } from "react-hot-toast";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import { filterBy } from "@progress/kendo-data-query";
import "@progress/kendo-theme-default/dist/all.css";
import { formatNumber } from "../../utils/format";
import useKendoGridState from "../../hooks/useKendoGridState";

// Rango Unicode de marcas diacríticas combinantes (acentos, tildes, etc.)
// que deja "normalize('NFD')" separadas de la letra base.
const MARCAS_DIACRITICAS = new RegExp(
  "[" + String.fromCharCode(0x300) + "-" + String.fromCharCode(0x36f) + "]",
  "g"
);

const quitarAcentos = (texto) => texto.normalize("NFD").replace(MARCAS_DIACRITICAS, "");

const normalizar = (texto) => quitarAcentos(texto.toLowerCase());

const nombreCompleto = (item) =>
  [item.nombre, item.apellido_paterno, item.apellido_materno]
    .filter(Boolean)
    .join(" ");

// Vista genérica para los rankings de campaña ("premios_campana"), donde
// "tipoCampana" distingue cada dinámica (futbolero1, futbolero2, ...).
// Se parametriza en vez de duplicar un componente por dinámica.
export default function RankingFutbolero({
  titulo,
  descripcion,
  notaPremio,
  tipoCampana,
  mesesCierre,
  columnaValorLabel = "Puntos",
  mostrarMonto = false,
  agruparPorZona = false,
}) {
  const { success, message, Posicion, RankingAddCampanaService } = useRanking();
  const { tokenFull, Roles } = useUser();
  const { sort, setSort, filter, setFilter, page, setPage, pageChange } =
    useKendoGridState(agruparPorZona ? "region" : "picision", "asc");
  const [busqueda, setBusqueda] = useState("");

  useEffect(() => {
    const data = new FormData();
    data.append("desde", mesesCierre);
    data.append("ano_cierre", new Date().getFullYear());
    data.append("tipo_campana", tipoCampana);

    RankingAddCampanaService(data, tokenFull);
  }, [RankingAddCampanaService, mesesCierre, tipoCampana, tokenFull]);

  useEffect(() => {
    if (success) {
      toast.dismiss();
      toast.success(message);
    }
  }, [message, success]);

  const handleBusqueda = (texto) => {
    setBusqueda(texto);
    setPage({ ...page, skip: 0 });
  };

  const busquedaNormalizada = normalizar(busqueda.trim());
  const datosEncontrados = busquedaNormalizada
    ? Posicion.filter((item) =>
        normalizar(nombreCompleto(item)).includes(busquedaNormalizada)
      )
    : Posicion;

  return (
    <>
      <Toaster />
      <h2 className="titulo2">{titulo}</h2>
      {(descripcion || notaPremio) && (
        <p className="text-muted">
          {[descripcion, notaPremio].filter(Boolean).join(" · ")}
        </p>
      )}
      <div className="row">
        <div className="col-md-4">
          <label>Busca tu nombre</label>
          <input
            type="text"
            className="form-control"
            placeholder="Escribe tu nombre..."
            value={busqueda}
            onChange={(e) => handleBusqueda(e.target.value)}
          />
        </div>
      </div>
      <br />
      <div className="row">
        <div className="col-md-12">
          <Grid
            sortable={true}
            sort={sort}
            onSortChange={(e) => setSort(e.sort)}
            filterable={true}
            filter={filter}
            onFilterChange={(e) => setFilter(e.filter)}
            pageable={true}
            data={filterBy(
              datosEncontrados.slice(page.skip, page.take + page.skip),
              filter
            )}
            skip={page.skip}
            take={page.take}
            total={datosEncontrados.length}
            onPageChange={pageChange}
            style={{ height: "620px" }}
          >
            <GridNoRecords>No hay informacion disponible</GridNoRecords>
            {agruparPorZona && (
              <GridColumn field="region" title="Zona" filter="text" />
            )}
            <GridColumn
              field="picision"
              title="Posición"
              filterable={false}
              format={null}
            />
            <GridColumn
              field="Vendedor"
              filterable={false}
              format={null}
              cell={(e) => (
                <td style={{ marginTop: 10 }}>{nombreCompleto(e.dataItem)}</td>
              )}
            />
            <GridColumn
              field="puntos"
              title={columnaValorLabel}
              filterable={false}
              format={null}
              cell={(e) => (
                <td style={{ marginTop: 10, textAlign: "right" }}>
                  {e.dataItem.puntos}
                </td>
              )}
            />
            {mostrarMonto && Roles === "Admin" && (
              <GridColumn
                field="monto"
                title="Monto"
                filterable={false}
                format={null}
                cell={(e) => (
                  <td style={{ marginTop: 10, textAlign: "right" }}>
                    {"$" + formatNumber(e.dataItem.monto)}
                  </td>
                )}
              />
            )}
          </Grid>
        </div>
      </div>
    </>
  );
}
